import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/sdk';
import { useAuth } from '@/auth/auth0-provider';

export interface SiteAnalyticsRange {
  days: number;
  from: string;
  to: string;
}

export interface SiteAnalyticsSeriesPoint {
  date: string;
  pageViews: number;
}

export interface SiteAnalyticsTopCountry {
  code: string;
  name: string;
  share: number;
}

export interface SiteAnalytics {
  range: SiteAnalyticsRange;
  pageViews: number;
  visitors: number;
  avgSessionMs: number | null;
  topCountry: SiteAnalyticsTopCountry | null;
  series: SiteAnalyticsSeriesPoint[];
}

interface Options {
  /** Defaults to 30. Backend clamps to one of {7, 14, 30}. */
  days?: number;
}

/**
 * Fetches Cloudflare Web Analytics for a single site.
 *
 * Returns zero-valued payload for sites with no traffic — the caller doesn't
 * need to special-case empty data, the analytics card just renders zeros.
 */
export function useSiteAnalytics(siteId: string | undefined, options: Options = {}) {
  const { isAuthenticated, isSdkReady } = useAuth();
  const days = options.days ?? 30;
  return useQuery<SiteAnalytics>({
    queryKey: ['sites', siteId, 'analytics', { days }],
    queryFn: () =>
      api.get<SiteAnalytics>(
        `/api/sites/${encodeURIComponent(siteId as string)}/analytics?days=${days}`,
      ),
    enabled: !!siteId && isAuthenticated && isSdkReady,
  });
}
